import { Arrow, Group, Label, Line, Rect, Tag, Text } from "react-konva";
import type { Furniture } from "../../lib/types";
import type { FitInfo } from "../../lib/useFitToRoom";
import { sinkTrackFor } from "../../lib/furniture/templates/countertops";
import { FURNITURE_SELECTED_STROKE } from "./FurnitureNode";

const ACCENT = FURNITURE_SELECTED_STROKE;

export interface SinkSlideHintProps {
  furniture: Furniture;
  fit: FitInfo;
  stageScale: number;
  /** Pointer is over the sink. The outline firms up and the cursor can change. */
  hot: boolean;
  /** Second click is down: the fixture is grabbed and ready to slide. */
  held: boolean;
}

/**
 * Shown while a counter or island is selected. A dashed ring and a slide
 * grip mark the sink as something you can double-click and drag along the
 * slab. While that drag is held, the ring fills in and the grip presses in.
 */
export function SinkSlideHint({ furniture: f, fit, stageScale, hot, held }: SinkSlideHintProps) {
  const track = sinkTrackFor(f);
  if (!track) return null;

  const stored = f.model?.kind === "parametric" ? f.model.params.sinkAt : undefined;
  const permille =
    typeof stored === "number" && stored >= 0 ? track.nearestValid(stored) : track.autoPermille;
  const pose = track.poseAt(permille);
  const loops = track.outlines(pose);

  const inv = 1 / Math.max(stageScale, 1e-6);
  const pxPerIn = fit.scale;
  const center = fit.pointToPx({ x: f.x, y: f.y });
  const firm = hot || held;

  let reach = 0;
  for (const loop of loops) {
    for (const poly of [loop.basin, loop.faucet]) {
      for (const p of poly) {
        const dx = (p.x - pose.x) * pxPerIn;
        const dy = (p.y - pose.y) * pxPerIn;
        reach = Math.max(reach, Math.hypot(dx, dy));
      }
    }
  }
  // Caption sits just to the right of the fixture, upright on screen,
  // so it doesn't cover the sink or the counter's edge handles.
  const captionX = reach * stageScale + 10;

  return (
    <Group x={center.x} y={center.y} rotation={f.rotation} listening={false}>
      <Group scaleX={pxPerIn} scaleY={pxPerIn}>
        {loops.flatMap((loop, i) =>
          [loop.basin, loop.faucet].map((poly, j) => (
            <Line
              key={`${i}-${j}`}
              points={poly.flatMap((p) => [p.x, p.y])}
              closed
              stroke={ACCENT}
              strokeWidth={held ? 2.5 : firm ? 2 : 1.5}
              strokeScaleEnabled={false}
              dash={firm ? undefined : [5, 3.5]}
              dashEnabled={!firm}
              fill={held ? "rgba(42, 112, 245, 0.3)" : undefined}
              lineJoin="round"
              perfectDrawEnabled={false}
            />
          ))
        )}
      </Group>

      <Group
        x={pose.x * pxPerIn}
        y={pose.y * pxPerIn}
        rotation={pose.angle}
        scaleX={inv}
        scaleY={inv}
      >
        <Rect
          x={-16}
          y={-8}
          width={32}
          height={16}
          cornerRadius={8}
          fill={held ? ACCENT : "#ffffff"}
          stroke={ACCENT}
          strokeWidth={1.5}
          shadowColor="#0b0d14"
          shadowBlur={4}
          shadowOpacity={0.25}
          shadowOffsetY={1}
        />
        <Arrow
          points={[-9, 0, 9, 0]}
          pointerAtBeginning
          pointerAtEnding
          pointerLength={5}
          pointerWidth={5}
          stroke={held ? "#ffffff" : ACCENT}
          fill={held ? "#ffffff" : ACCENT}
          strokeWidth={1.6}
        />
      </Group>

      {!held && (
        <Group
          x={pose.x * pxPerIn}
          y={pose.y * pxPerIn}
          rotation={-f.rotation}
          scaleX={inv}
          scaleY={inv}
        >
          <Label x={captionX} y={-11}>
            <Tag fill="#15151a" cornerRadius={6} opacity={0.92} />
            <Text
              text="Double-click to slide"
              width={124}
              align="center"
              fontSize={11}
              fontFamily="Inter, sans-serif"
              fontStyle="600"
              fill="#ffffff"
              padding={5}
            />
          </Label>
        </Group>
      )}
    </Group>
  );
}
